/**
 * Academy AI Tutor — Chat Modal.
 * A floating button on lesson screens opens this modal.
 * Student can ask any question about the lesson, in their native language.
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, Sparkles, Bot, RefreshCw } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const QUICK_PROMPTS_FR = [
  "Donne-moi une phrase d'exemple avec ce vocabulaire.",
  "Quelle est la différence entre ces mots ?",
  "Comment retenir tous ces mots facilement ?",
  "Pose-moi une question pour me tester.",
];

const QUICK_PROMPTS_EN = [
  "Give me an example sentence with this vocabulary.",
  "What's the difference between these words?",
  "How can I memorize all these words easily?",
  "Ask me a question to test me.",
];

/**
 * Props:
 *  - open: bool
 *  - onClose: fn
 *  - lessonId: string
 *  - lessonTitle: string
 *  - lessonItems: [{src, tgt, note}]
 *  - direction: 'fr2en' | 'en2fr'
 *  - lang: 'fr' | 'en' (UI)
 */
export default function AcademyTutorChat({ open, onClose, lessonId, lessonTitle, lessonItems = [], direction = 'fr2en', lang = 'fr' }) {
  const fr = lang === 'fr';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `tutor_chat_${Math.random().toString(36).slice(2, 12)}`);
  const endRef = useRef(null);

  // Body lock + ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Reset when closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => { setMessages([]); setInput(''); }, 300);
    } else {
      // Show greeting
      setMessages([{
        role: 'assistant',
        content: fr
          ? `Salut ! Je suis ton coach JoSmartTech pour cette leçon **« ${lessonTitle || 'sans titre'} »**. Pose-moi n'importe quelle question sur le vocabulaire — exemples, prononciation, différences, astuces de mémo... 🎯`
          : `Hi! I'm your JoSmartTech coach for the lesson **"${lessonTitle || 'untitled'}"**. Ask me anything about the vocabulary — examples, pronunciation, differences, memory tips... 🎯`,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    const userMsg = { role: 'user', content: message };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/academy/tutor/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message,
          lesson_id: lessonId || '',
          lesson_title: lessonTitle || '',
          lesson_items: lessonItems,
          direction,
          ui_language: lang,
          history: next.slice(-8).map(m => ({ role: m.role, content: m.content })),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur');
      }
      const d = await r.json();
      setMessages(m => [...m, { role: 'assistant', content: d.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: `❌ ${e.message}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const prompts = fr ? QUICK_PROMPTS_FR : QUICK_PROMPTS_EN;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="tutor-chat-modal">
        <motion.div
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          className="bg-slate-900 border border-emerald-400/20 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ height: '85vh', maxHeight: '85vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-emerald-500/10 to-cyan-500/5 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                <Bot size={16} className="text-emerald-300" />
              </div>
              <div>
                <div className="text-emerald-300/80 text-[10px] uppercase tracking-wider font-semibold">
                  {fr ? 'Coach JoSmartTech' : 'JoSmartTech coach'}
                </div>
                <div className="text-white font-bold text-sm truncate max-w-[280px]">{lessonTitle || (fr ? 'Leçon' : 'Lesson')}</div>
              </div>
            </div>
            <button onClick={onClose} className="text-white/40 hover:text-white p-2 rounded-lg hover:bg-white/5 transition" data-testid="tutor-chat-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => <Bubble key={i} msg={m} />)}
            {loading && (
              <div className="flex items-center gap-2 text-emerald-300/80 text-sm pl-1">
                <Loader2 size={14} className="animate-spin" />
                {fr ? 'Le coach réfléchit...' : 'Coach is thinking...'}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Quick prompts */}
          {messages.length <= 1 && !loading && (
            <div className="px-4 pb-2 shrink-0">
              <div className="text-white/40 text-[10px] uppercase tracking-wider font-semibold mb-2">
                {fr ? 'Questions rapides' : 'Quick prompts'}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {prompts.map((p, i) => (
                  <button key={i} onClick={() => send(p)}
                    className="text-xs px-3 py-1.5 rounded-full border border-emerald-400/25 bg-emerald-500/[0.06] text-emerald-200 hover:bg-emerald-500/15 transition"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 sm:p-4 border-t border-white/10 bg-white/[0.02] shrink-0">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                disabled={loading}
                placeholder={fr ? 'Pose ta question au coach...' : 'Ask the coach...'}
                className="flex-1 resize-none bg-white/[0.04] border border-white/10 focus:border-emerald-400/60 focus:outline-none text-white placeholder-white/30 rounded-xl px-3 py-2.5 text-sm transition"
                data-testid="tutor-chat-input"
                style={{ maxHeight: '120px' }}
              />
              <button onClick={() => send()} disabled={loading || !input.trim()}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold px-4 rounded-xl transition flex items-center justify-center"
                data-testid="tutor-chat-send">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <div className="flex justify-between items-center mt-2 text-[10px] text-white/30">
              <span>{fr ? 'Entrée = envoyer · Maj+Entrée = saut de ligne' : 'Enter = send · Shift+Enter = newline'}</span>
              <button onClick={() => setMessages([messages[0]])} className="hover:text-white/60 flex items-center gap-1">
                <RefreshCw size={10} /> {fr ? 'Nouvelle conversation' : 'New chat'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Bubble({ msg }) {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-emerald-500/15 border border-emerald-400/25 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center shrink-0">
        <Sparkles size={13} className="text-emerald-300" />
      </div>
      <div className={`max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${msg.error ? 'bg-rose-500/10 border border-rose-400/30 text-rose-200' : 'bg-white/[0.04] border border-white/10 text-white/90'}`}>
          {renderBold(msg.content)}
      </div>
    </div>
  );
}

function renderBold(text) {
  const parts = (text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} className="text-emerald-200 font-semibold">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}
